import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMemberApp } from '../AppProvider';
import { careHeadline, personalizedStarters } from '../careProfile';
import { createDraftConversation, listPendingDocuments } from '../localStore';
import Composer from '../components/Composer';
import { IconArrow } from '../components/Icons';
import { memberPath } from '@/config/hosts';
import { memberApi } from '../api';

function greetingName(user) {
  const raw = user?.full_name || user?.name || '';
  const first = raw.split(' ')[0];
  const hour = new Date().getHours();
  const when = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
  return first ? `${when}, ${first}` : when;
}

const EXPLAIN_PROMPT =
  'I attached my report. Please read it and explain the names and numbers in plain language. What should I confirm with my oncology team, and what is worth asking at the next visit? Do not diagnose or change treatment.';

export default function HomePage() {
  const { user, careProfile, setConversations, setUpgradeOpen, isPro, freeRemaining } = useMemberApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [opening, setOpening] = useState('');
  const docKickoffRef = useRef(false);
  const starters = useMemo(() => personalizedStarters(careProfile), [careProfile]);
  const lede = careHeadline(careProfile) || 'How can I help you today?';

  const start = (text, title, meta = {}) => {
    if (opening) return;
    if (!isPro) {
      setUpgradeOpen(true);
      return;
    }
    setOpening(title || text);
    const pending = listPendingDocuments(user);
    const documentIds = Array.from(
      new Set([...(meta.documentIds || []), ...pending.map((row) => row.id)])
    ).filter(Boolean);
    const draft = createDraftConversation(user, {
      title: title || text,
      text,
      tool: meta.tool,
      documentIds,
    });
    setConversations((prev) => [draft.conversation, ...prev.filter((c) => c.id !== draft.conversation.id)]);
    navigate(memberPath(`/c/${draft.conversation.id}`), { state: { pending: true } });
  };

  useEffect(() => {
    const docId = searchParams.get('doc');
    if (!docId || docKickoffRef.current) return;
    docKickoffRef.current = true;
    setSearchParams({}, { replace: true });

    (async () => {
      let title = 'Explain my report';
      try {
        const docs = await memberApi.documents();
        const match = (docs.documents || []).find((row) => row.id === docId);
        if (match?.filename) title = `Explain ${match.filename}`;
      } catch {
        /* still open chat with the id */
      }
      start(EXPLAIN_PROMPT, title, { documentIds: [docId], tool: 'labs' });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="by-stage">
      <div className="by-center" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div className="by-hello">
          <h1>{greetingName(user)}</h1>
          <p>{lede}</p>
        </div>
        <div className="by-prompts">
          {starters.map((item) => (
            <button
              key={item.id || item.title}
              type="button"
              className={`by-prompt${opening === item.title ? ' is-open' : ''}`}
              onClick={() => start(item.text, item.title, { tool: item.id })}
              disabled={!!opening}
            >
              <span>
                <b>{item.title}</b>
                <small>{opening === item.title ? 'Opening…' : item.label}</small>
              </span>
              <IconArrow />
            </button>
          ))}
        </div>
        <Composer
          onSend={(text, meta) => start(text, text, meta)}
          disabled={!!opening}
          autoFocus={false}
          placeholder="Ask about treatment, food, medicines, or clinic"
        />
      </div>
    </div>
  );
}
